import { Fragment } from 'react';
import { Box, Grid, Typography, Divider } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { ThemePalette } from 'mui.theme';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import {
  addLiveDataToAssets,
  assetTypeMappings,
  getAssetTypeLabelKey,
} from 'views/FleetOverview/helpers/asset.helpers';
import type { Asset } from 'types/asset.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AssetHeader = ({
  enableDarkTheme,
  selectedAsset,
  latestTagData,
  hierarchyInfo,
}: {
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  latestTagData: any;
  hierarchyInfo: any;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const pngName = getImageName(selectedAsset, 'Small');
  const assetsWithLiveData = addLiveDataToAssets(
    [selectedAsset],
    latestTagData
  );

  const assetDetails = [
    {
      label: t('asset_id'),
      value: selectedAsset.bumperNumber,
    },
    {
      label: t('loc_importer_col_type'),
      value: t(
        getAssetTypeLabelKey(assetTypeMappings[selectedAsset.assetType])
      ),
    },
    {
      label: t('hierarchy_node'),
      value: hierarchyInfo.nodeDisplay,
    },
    {
      label: 'OEM',
      value: selectedAsset.make,
    },
    {
      label: t('model_10'),
      value: selectedAsset.model,
    },
    {
      label: t('cdp_step_status'),
      value: selectedAsset.isActive ? 'Operational' : 'Out Of Service',
    },
  ];

  return (
    <Grid item sm={12} md={12} lg={7} xs={12}>
      <Box
        sx={{
          marginLeft: '10px',
          borderRadius: '5px',
          height: '100px',
        }}
      >
        <Typography
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('asset_details')}
          <span
            style={{
              fontSize: '11px',
              paddingLeft: '10px',
              color: ThemePalette.typography.grey,
            }}
          >
            {t('last_ingested_time')}
          </span>
          <span
            style={{
              fontSize: '11px',
              fontWeight: '600',
              paddingLeft: '5px',
            }}
          >
            {Array.isArray(latestTagData)
              ? undefined
              : convertUtcToDisplayTime(
                  latestTagData[selectedAsset.device?.deviceId]?.ingestDate,
                  preferredTimeDisplayMode,
                  timezone,
                  'YYYY-MMM-DD HH:mm'
                )}
          </span>
        </Typography>
        <Box
          sx={{
            paddingTop: '10px',
          }}
        >
          <Grid2
            container
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            {/* Render asset details */}
            {assetDetails.map((data, index, arr) => (
              <Fragment key={`${data.label}-group`}>
                <Grid2 key={`${data.label}-1`} display="flex">
                  {index === 0 && (
                    <PNG
                      name={pngName}
                      alt={selectedAsset.bumperNumber}
                      style={{
                        height: '70px',
                        width: '90px',
                        marginLeft: '-20px',
                        marginTop: '-10px',
                        zIndex: 1,
                      }}
                    />
                  )}
                  <div>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontSize: '11px',
                        textAlign: 'center',
                        paddingBottom: '5px',
                      }}
                    >
                      {data.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                        }}
                      >
                        {data.value}
                      </Box>
                    </Typography>
                  </div>
                </Grid2>
                <Grid2 padding={0}>
                  {index < arr.length - 1 && (
                    <Divider
                      key={`${data.label}-divider`}
                      orientation="vertical"
                      sx={{
                        background: palette.borderGrey,
                      }}
                    />
                  )}
                </Grid2>
              </Fragment>
            ))}
          </Grid2>
        </Box>
      </Box>
    </Grid>
  );
};
